import { X } from 'lucide-react';

/**
 * Documentación de la sección Bajas: el flujo de punta a punta y qué significa
 * cada clasificación de la revisión.
 *
 * Se abre sola la primera vez que alguien entra a la sección (ver
 * `useVistoUnaVez`) y después queda a mano en el signo de pregunta del header.
 *
 * El contenido está escrito desde el lado del operador: qué ve, qué tiene que
 * hacer y por qué una fila no se procesó. Los nombres técnicos de las
 * clasificaciones no aparecen — el operador ve el texto de la pantalla, no
 * `fallecimiento_con_grupo`.
 */
export function AyudaBajasModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cómo funcionan las bajas"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,37,100,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 50,
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(760px, 100%)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          padding: 0,
        }}
      >
        <header
          className="rowBetween"
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-outline)',
            alignItems: 'center',
          }}
        >
          <h2 className="heading-5" style={{ margin: 0 }}>
            Cómo funcionan las bajas
          </h2>
          <button type="button" className="btn btn-ghost btn--sm" onClick={onClose}>
            <X size={18} />
          </button>
        </header>

        <div style={{ overflow: 'auto', padding: '18px 20px 22px', display: 'grid', gap: 22 }}>
          <p className="text-element-mid" style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55 }}>
            Esta sección da de baja gente en Binary a partir de un archivo. Cada persona se cruza
            contra Binary <strong>en vivo</strong>, se revisa fila por fila y recién ahí se confirma.
            Nada se manda sin que lo apruebes.
          </p>

          <Seccion titulo="El flujo, paso a paso">
            <Paso n={1} titulo="Descargar la plantilla">
              El botón está arriba del todo, en el paso Cargar. Son cuatro columnas:{' '}
              <b>DNI</b>, <b>MOTIVO</b>, <b>FECHA_VIGENCIA</b> y <b>FECHA_FALLECIMIENTO</b>. Podés
              agregar una columna <b>LEGAJO</b> si tenés DNI repetidos entre organizaciones.
            </Paso>
            <Paso n={2} titulo="Cargar el archivo">
              Se busca a cada persona en el padrón por DNI y después se la resuelve en Binary
              probando por CUIL, por afi_id y por web_id, en ese orden. La pantalla te dice con cuál
              la encontró.
            </Paso>
            <Paso n={3} titulo="Revisar">
              Cada fila queda con un estado. Lo que se puede arreglar se arregla{' '}
              <strong>en la misma pantalla</strong> —motivo, vigencia, fecha de fallecimiento— sin
              volver a subir el archivo. Mientras quede algo para corregir, no se puede avanzar.
            </Paso>
            <Paso n={4} titulo="Confirmar">
              Se manda el lote. Vas viendo el avance y, al terminar, el resultado de cada persona.
              Lo que no se pudo procesar queda en <b>Alertas Bajas</b> con el motivo.
            </Paso>
          </Seccion>

          <Seccion titulo="Los dos motivos que se aceptan">
            <Fila
              etiqueta="36010 · Baja pedida por empresa"
              texto="La vigencia se fuerza siempre al 1° del mes próximo, sin importar qué diga el archivo."
            />
            <Fila
              etiqueta="37020 · Fallecimiento"
              texto="Pide la fecha real del deceso. La vigencia la calcula el sistema: el 1° del mes siguiente al deceso (las vigencias son siempre 1° de mes); lo que diga el archivo se ignora."
            />
            <p className="text-element-mid" style={{ margin: '4px 0 0', fontSize: 13.5 }}>
              Cualquier otro código queda para corregir. La columna es libre, así que es fácil que
              se cuele uno viejo de la serie 200, que Binary dejó de usar hace años.
            </p>
          </Seccion>

          <Seccion titulo="Bajar a un titular">
            <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55 }}>
              Bajar a un titular es bajar el legajo completo, así que solo se permite de dos formas:
            </p>
            <Fila
              etiqueta="Titular sin familia activa"
              texto="El legajo es él solo, así que bajarlo ya es bajar el legajo entero."
            />
            <Fila
              etiqueta="Titular con TODO su grupo en el archivo"
              texto="Binary da de baja al grupo entero al bajar al titular. Los integrantes aparecen como dados de baja por réplica: no se los manda dos veces."
            />
            <p className="text-element-mid" style={{ margin: '4px 0 0', fontSize: 13.5 }}>
              Si el titular viene pero deja integrantes activos afuera, la fila no se da de baja
              sola: primero hay que decidir con quién queda cada uno, con el botón{' '}
              <strong>Rearmar grupo</strong> de la misma fila. El grupo se controla contra Binary,
              no contra el padrón — si al padrón le falta alguien que Binary tiene activo, cuenta
              igual.
            </p>
          </Seccion>

          <Seccion titulo="Rearmar el grupo">
            <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55 }}>
              Cuando el titular se da de baja y queda familia, el grupo no se cae: alguien pasa a
              ser titular y el resto sigue con él. La pantalla lo pregunta{' '}
              <strong>de a una persona</strong>: primero quién queda a cargo del legajo, y después,
              uno por uno, con quién queda cada integrante.
            </p>
            <Fila
              etiqueta="Quién puede quedar a cargo"
              texto="El cónyuge, sin condiciones. Un hijo de hasta 25 años. De 26 en adelante solo con discapacidad declarada. Adherentes y nietos no pueden: aparecen en la lista, apagados y con el motivo."
            />
            <Fila
              etiqueta="Menores y casos con curador"
              texto="Un menor de 18 puede quedar a cargo, pero hay que consignarle un tutor (y un curador en el caso de discapacidad). Esa carga todavía la hace el operador, así que esos casos siguen frenados."
            />
            <Fila
              etiqueta="Puede quedar más de un grupo"
              texto="Si dos hijos grandes arman cada uno el suyo, se abre un legajo nuevo por grupo, derivado del original. El primero se queda con el legajo actual."
            />
            <Fila
              etiqueta="Hay que repartir a TODOS"
              texto="No se puede confirmar mientras quede alguien sin decidir: Binary reemplaza el grupo entero, así que quien no quede asignado se queda sin cobertura."
            />
            <p className="text-element-mid" style={{ margin: '4px 0 0', fontSize: 13.5 }}>
              Al confirmar, la baja se hace <strong>en el momento</strong>: no entra en el lote ni
              pasa por Alertas. Son dos solicitudes en Binary —armar el grupo y después bajar al
              saliente, en ese orden— y no se deshacen solas. Si nadie del grupo puede quedar a
              cargo, la baja se lleva el legajo completo y eso sí queda en Alertas Bajas.
            </p>
          </Seccion>

          <Seccion titulo="Qué gente podés dar de baja">
            <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55 }}>
              Si entrás como usuario de una organización, todo lo de esta pantalla —el listado, la
              búsqueda, el archivo y Alertas— es <strong>solo de tu organización</strong>. Una
              persona de otra empresa no aparece: no es que no tengas permiso para verla, es que
              para esta pantalla no existe.
            </p>
            <p className="text-element-mid" style={{ margin: '4px 0 0', fontSize: 13.5 }}>
              Si el archivo trae un DNI que no es de tu gente, esa fila se informa como no
              encontrada. El control lo hace el servidor al confirmar, persona por persona, así que
              tampoco se puede saltear armando el pedido a mano.
            </p>
          </Seccion>

          <Seccion titulo="Por qué una fila no se procesa">
            <Fila etiqueta="Para corregir acá" texto="Falta el motivo, el motivo no está habilitado, falta la fecha de fallecimiento, o la vigencia es anterior al deceso." />
            <Fila etiqueta="Va a Alertas" texto="La persona es de plan MS 2 (esta etapa da de baja solo MS 1), es un fallecimiento de un titular con grupo, o es un caso retroactivo que hay que hacer a mano." />
            <Fila etiqueta="No se toca" texto="Ya estaba de baja, no está activa en Binary, no se encontró, o el DNI está repetido entre organizaciones y hace falta el legajo para desambiguar." />
            <Fila etiqueta="Repetida en el archivo" texto="La primera aparición se procesa; las siguientes se ignoran. Es a propósito: mandarla dos veces crearía dos solicitudes de baja y la segunda no se deshace fácil." />
          </Seccion>

          <Seccion titulo="Después de confirmar">
            <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55 }}>
              La baja no es instantánea: Binary la programa para la fecha de vigencia y recién ahí
              la persona pasa a inactiva. En <b>Últimas bajas</b> ves lo que se mandó, con su número
              de solicitud, y podés buscar por DNI, CUIL, nombre o legajo. En <b>Alertas Bajas</b>{' '}
              quedan los casos manuales y los rechazos de Binary; cuando resolvés uno, "Marcar como
              gestionada" verifica contra Binary antes de cerrarlo.
            </p>
          </Seccion>
        </div>

        <footer
          style={{
            padding: '14px 20px',
            borderTop: '1px solid var(--color-outline)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Entendido
          </button>
        </footer>
      </div>
    </div>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section style={{ display: 'grid', gap: 10 }}>
      <h3
        style={{
          margin: 0,
          fontSize: 12,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--color-element-mid)',
        }}
      >
        {titulo}
      </h3>
      {children}
    </section>
  );
}

function Paso({ n, titulo, children }: { n: number; titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <span
        aria-hidden
        style={{
          flex: '0 0 auto',
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'var(--color-primary)',
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 1,
        }}
      >
        {n}
      </span>
      <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.5 }}>
        <strong>{titulo}.</strong> {children}
      </p>
    </div>
  );
}

function Fila({ etiqueta, texto }: { etiqueta: string; texto: string }) {
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <span style={{ fontSize: 13.5, fontWeight: 650 }}>{etiqueta}</span>
      <span className="text-element-mid" style={{ fontSize: 13.5, lineHeight: 1.45 }}>
        {texto}
      </span>
    </div>
  );
}
